import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper, validateSync } from '../../helpers';
import { errorStateConstants } from '../../components/layout/treatment-plan/medical-history/medicalHistoryConstants';
import { MedicalHistoryRowSchema } from '../../components/layout/treatment-plan/medical-history/validationSchema';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  IReferralAssessmentTool,
  IDiagnosis,
  IMedicalHistory,
  IReferralHistory,
  IDiagnosisIncomplete,
} from '../../utils/types';

const name = 'medicalHistory';
const initialState = createInitialState();
// SAFETY: Redux Toolkit cannot infer the nested reducer factory payloads.
const reducers = createReducers() as any;
// SAFETY: These legacy thunk payloads are untyped at their API boundary.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<MedicalHistoryState>) =>
    createExtraReducers(builder),
});

export interface MedicalHistoryState {
  treatmentPlanId: number | null;
  medicalHistory: IMedicalHistory | Partial<IMedicalHistory>;
  diagnoses: Array<IDiagnosis>;
  unsavedDiagnoses: Array<IDiagnosis>;
  unfinishedDiagnosis: IDiagnosisIncomplete | null;
  referralHistory: IReferralHistory;
  error: any;
  loading: boolean;
  referralHistoryLoading: boolean;
  errorState: any;
}

type MedicalHistoryStateUpdate = {
  key: keyof MedicalHistoryState | 'triggerValidationsOnPageLoad';
  data: any;
};

function createInitialState(): MedicalHistoryState {
  return {
    treatmentPlanId: null,
    medicalHistory: {},
    diagnoses: [],
    unsavedDiagnoses: [],
    unfinishedDiagnosis: null,
    referralHistory: {
      referring_provider_name: '',
      reason_for_referral: '',
      assessment_tools: [],
      date_of_diagnosis: null,
    },
    loading: false,
    referralHistoryLoading: false,
    error: null,
    errorState: {},
  };
}

function createReducers() {
  return {
    clearState,
    updateErrorState,
    addUnsavedDiagnosis,
    updateUnsavedDiagnosis,
    removeUnsavedDiagnosis,
    updateState,
  };

  function updateState(
    state: MedicalHistoryState,
    action: PayloadAction<MedicalHistoryStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function addUnsavedDiagnosis(state: MedicalHistoryState, action: any) {
    const { data, id } = action.payload;
    state.unsavedDiagnoses = [...state.unsavedDiagnoses, { ...data, id }];
    state.unfinishedDiagnosis = null;
  }

  function updateUnsavedDiagnosis(state: MedicalHistoryState, action: any) {
    const { data, id } = action.payload;
    state.unsavedDiagnoses = state.unsavedDiagnoses.map((diagnosis) =>
      diagnosis.id === id ? data : diagnosis,
    );
  }

  function removeUnsavedDiagnosis(state: MedicalHistoryState, action: any) {
    const { data: _data, id } = action.payload;
    state.unsavedDiagnoses = state.unsavedDiagnoses.filter((diagnosis) => diagnosis.id !== id);
  }

  function updateErrorState(state: MedicalHistoryState, action: any) {
    state.errorState = { ...state.errorState, ...action.payload };
  }

  function clearState(state: MedicalHistoryState) {
    state.treatmentPlanId = null;
    state.medicalHistory = {};
    state.diagnoses = [];
    state.unsavedDiagnoses = [];
    state.unfinishedDiagnosis = null;
    state.referralHistory = {
      referring_provider_name: '',
      reason_for_referral: '',
      assessment_tools: [],
      date_of_diagnosis: '',
    };
    state.loading = false;
    state.referralHistoryLoading = false;
    state.error = null;
    state.errorState = {};
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getMedicalHistory: getMedicalHistory(),
    createMedicalHistory: createMedicalHistory(),
    updateMedicalHistory: updateMedicalHistory(),
    getDiagnoses: getDiagnoses(),
    createDiagnosis: createDiagnosis(),
    createMultipleDiagnoses: createMultipleDiagnoses(),
    updateDiagnosis: updateDiagnosis(),
    deleteDiagnosis: deleteDiagnosis(),
    getReferralHistory: getReferralHistory(),
    createReferralHistory: createReferralHistory(),
    updateReferralHistory: updateReferralHistory(),
    getAssessmentTools: getAssessmentTools(),
    createMultipleAssessmentTools: createMultipleAssessmentTools(),
    createAssesmentTool: createAssesmentTool(),
    updateAssesmentTool: updateAssesmentTool(),
    deleteAssesmentTool: deleteAssesmentTool(),
  };

  function getMedicalHistory() {
    return createAsyncThunk(
      `${name}/getMedicalHistory`,
      async ({ getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/medical_history/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createMedicalHistory() {
    return createAsyncThunk(
      `${name}/createMedicalHistory`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${planId}/medical_history/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateMedicalHistory() {
    return createAsyncThunk(
      `${name}/updateMedicalHistory`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/medical_history/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function getDiagnoses() {
    return createAsyncThunk(
      `${name}/getDiagnoses`,
      async ({ getAccessTokenSilently, planId, medicalHistoryId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/medical_history/${medicalHistoryId}/diagnoses/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createMultipleDiagnoses() {
    return createAsyncThunk(
      `${name}/createMultipleDiagnoses`,
      async ({ diagnoses, getAccessTokenSilently, planId, medicalHistoryId }: any) => {
        const promises = diagnoses.map((data: IDiagnosis) =>
          fetchWrapper.post(
            {
              url: `${baseUrl}/${planId}/medical_history/${medicalHistoryId}/diagnoses/`,
              getAccessTokenSilently,
            },
            data,
          ),
        );
        const response = await Promise.all(promises);
        return response;
      },
    );
  }

  function createDiagnosis() {
    return createAsyncThunk(
      `${name}/createDiagnosis`,
      async ({ data, getAccessTokenSilently, planId, medicalHistoryId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${planId}/medical_history/${medicalHistoryId}/diagnoses/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateDiagnosis() {
    return createAsyncThunk(
      `${name}/updateDiagnosis`,
      async ({ data, getAccessTokenSilently, planId, medicalHistoryId, diagnosisId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/medical_history/${medicalHistoryId}/diagnoses/${diagnosisId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteDiagnosis() {
    return createAsyncThunk(
      `${name}/deleteDiagnosis`,
      async ({ getAccessTokenSilently, planId, medicalHistoryId, diagnosisId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/medical_history/${medicalHistoryId}/diagnoses/${diagnosisId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getReferralHistory() {
    return createAsyncThunk(
      `${name}/getReferralHistory`,
      async ({ getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/referral_history/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createReferralHistory() {
    return createAsyncThunk(
      `${name}/createReferralHistory`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${planId}/referral_history/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateReferralHistory() {
    return createAsyncThunk(
      `${name}/updateReferralHistory`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/referral_history/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function getAssessmentTools() {
    return createAsyncThunk(
      `${name}/getAssessmentTools`,
      async ({ getAccessTokenSilently, planId, referralHistoryId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/referral_history/${referralHistoryId}/assessment_tools/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createMultipleAssessmentTools() {
    return createAsyncThunk(
      `${name}/createMultipleAssessmentTools`,
      async ({ assessmentTools, getAccessTokenSilently, planId, referralHistoryId }: any) => {
        const promises = assessmentTools.map((data: IReferralAssessmentTool) =>
          fetchWrapper.post(
            {
              url: `${baseUrl}/${planId}/referral_history/${referralHistoryId}/assessment_tools/`,
              getAccessTokenSilently,
            },
            data,
          ),
        );
        const response = await Promise.all(promises);
        return response;
      },
    );
  }

  function createAssesmentTool() {
    return createAsyncThunk(
      `${name}/createAssesmentTool`,
      async ({ data, getAccessTokenSilently, planId, referralHistoryId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${planId}/referral_history/${referralHistoryId}/assessment_tools/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateAssesmentTool() {
    return createAsyncThunk(
      `${name}/updateAssesmentTool`,
      async ({ data, getAccessTokenSilently, planId, referralHistoryId, toolId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/referral_history/${referralHistoryId}/assessment_tools/${toolId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteAssesmentTool() {
    return createAsyncThunk(
      `${name}/deleteAssesmentTool`,
      async ({ getAccessTokenSilently, planId, referralHistoryId, toolId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/referral_history/${referralHistoryId}/assessment_tools/${toolId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<MedicalHistoryState>) {
  const {
    getMedicalHistory,
    createMedicalHistory,
    updateMedicalHistory,
    getDiagnoses,
    createMultipleDiagnoses,
    createDiagnosis,
    updateDiagnosis,
    deleteDiagnosis,
    getReferralHistory,
    createReferralHistory,
    updateReferralHistory,
    createAssesmentTool,
    updateAssesmentTool,
    deleteAssesmentTool,
  } = extraActions;

  builder
    // getMedicalHistory()
    .addCase(getMedicalHistory.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      getMedicalHistory.fulfilled,
      (
        state: MedicalHistoryState,
        action: PayloadAction<IMedicalHistory, string, { arg: { forceUpdate: boolean } }>,
      ) => {
        if (
          action.meta.arg.forceUpdate ||
          (action.payload &&
            Object.keys(action.payload).length !== 0 &&
            (!state.medicalHistory || Object.keys(state.medicalHistory).length === 0))
        ) {
          state.medicalHistory = !action.payload?.id ? state.medicalHistory : action.payload;
        }
        state.loading = false;
      },
    )
    .addCase(getMedicalHistory.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createMedicalHistory()
    .addCase(createMedicalHistory.pending, (state: MedicalHistoryState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createMedicalHistory.fulfilled,
      (state: MedicalHistoryState, action: PayloadAction<IMedicalHistory>) => {
        state.loading = false;
        state.medicalHistory = action.payload;
      },
    )
    .addCase(createMedicalHistory.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    .addCase(updateMedicalHistory.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateMedicalHistory.fulfilled, (state: MedicalHistoryState) => {
      state.loading = false;
    })
    .addCase(updateMedicalHistory.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getDiagnoses()
    .addCase(getDiagnoses.pending, (state: MedicalHistoryState) => {
      state.error = null;
    })
    .addCase(
      getDiagnoses.fulfilled,
      (state: MedicalHistoryState, action: PayloadAction<Array<IDiagnosis>>) => {
        state.diagnoses = action.payload;
      },
    )
    .addCase(getDiagnoses.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      toast.error(state.error.message);
    })
    // createMultipleDiagnoses()
    .addCase(createMultipleDiagnoses.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      createMultipleDiagnoses.fulfilled,
      (state: MedicalHistoryState, action: PayloadAction<IDiagnosis[]>) => {
        state.diagnoses = [...state.diagnoses, ...action.payload];
        state.loading = false;
      },
    )
    .addCase(createMultipleDiagnoses.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createDiagnosis()
    .addCase(createDiagnosis.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      createDiagnosis.fulfilled,
      (state: MedicalHistoryState, action: PayloadAction<IDiagnosis>) => {
        if (validateSync(MedicalHistoryRowSchema, state.unfinishedDiagnosis)) {
          state.unfinishedDiagnosis = null;
        }
        state.diagnoses = [...state.diagnoses, action.payload];
        state.loading = false;
      },
    )
    .addCase(createDiagnosis.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateDiagnosis()
    .addCase(updateDiagnosis.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateDiagnosis.fulfilled, (state: MedicalHistoryState, action: any) => {
      state.diagnoses = state.diagnoses.map((dignosis) => {
        if (dignosis.id === action.meta.arg.diagnosisId) {
          return action.payload;
        }
        return dignosis;
      });
      state.loading = false;
    })
    .addCase(updateDiagnosis.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // deleteDiagnosis()
    .addCase(deleteDiagnosis.pending, (state: MedicalHistoryState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      deleteDiagnosis.fulfilled,
      (
        state: MedicalHistoryState,
        action: PayloadAction<IDiagnosis, string, { arg: { diagnosisId: number } }>,
      ) => {
        const newErrorState = { ...state.errorState };
        delete newErrorState[
          `${errorStateConstants.medicalHistoryRow}-${action.meta.arg.diagnosisId}`
        ];
        state.loading = false;
        state.diagnoses = state.diagnoses.filter(
          (diagnosis) => diagnosis.id !== action.meta.arg.diagnosisId,
        );
        state.errorState = newErrorState;
      },
    )
    .addCase(deleteDiagnosis.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getReferralHistory()
    .addCase(getReferralHistory.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.referralHistoryLoading = true;
    })
    .addCase(
      getReferralHistory.fulfilled,
      (
        state: MedicalHistoryState,
        action: PayloadAction<IReferralHistory, string, { arg: { forceUpdate: boolean } }>,
      ) => {
        state.referralHistory = !action.payload?.id ? state.referralHistory : action.payload;
        state.referralHistoryLoading = false;
      },
    )
    .addCase(getReferralHistory.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.referralHistoryLoading = false;
      toast.error(state.error.message);
    })
    // createReferralHistory()
    .addCase(createReferralHistory.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.referralHistoryLoading = true;
    })
    .addCase(
      createReferralHistory.fulfilled,
      (state: MedicalHistoryState, action: PayloadAction<IReferralHistory>) => {
        state.referralHistory = action.payload;
        state.referralHistoryLoading = false;
      },
    )
    .addCase(createReferralHistory.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.referralHistoryLoading = false;
      toast.error(state.error.message);
    })
    // updateReferralHistory()
    .addCase(updateReferralHistory.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.referralHistoryLoading = true;
    })
    .addCase(updateReferralHistory.fulfilled, (state: MedicalHistoryState) => {
      state.referralHistoryLoading = false;
    })
    .addCase(updateReferralHistory.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.referralHistoryLoading = false;
      toast.error(state.error.message);
    })
    // createAssesmentTool()
    .addCase(createAssesmentTool.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.referralHistoryLoading = true;
    })
    .addCase(
      createAssesmentTool.fulfilled,
      (state: MedicalHistoryState, action: PayloadAction<IReferralAssessmentTool>) => {
        state.referralHistory = {
          ...state.referralHistory,
          assessment_tools: state.referralHistory?.assessment_tools
            ? [...state.referralHistory.assessment_tools, action.payload]
            : [action.payload],
        };
        state.referralHistoryLoading = false;
      },
    )
    .addCase(createAssesmentTool.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.referralHistoryLoading = false;
      toast.error(state.error.message);
    })
    // updateAssesmentTool()
    .addCase(updateAssesmentTool.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.referralHistoryLoading = true;
    })
    .addCase(updateAssesmentTool.fulfilled, (state: MedicalHistoryState, action: any) => {
      state.referralHistory.assessment_tools = [
        ...(state.referralHistory.assessment_tools || []),
      ].map((tool) => {
        if (tool.id === action.meta.arg.toolId) {
          return action.payload;
        }
        return tool;
      });
      state.referralHistoryLoading = false;
    })
    .addCase(updateAssesmentTool.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.referralHistoryLoading = false;
      toast.error(state.error.message);
    })
    // deleteAssesmentTool()
    .addCase(deleteAssesmentTool.pending, (state: MedicalHistoryState) => {
      state.error = null;
      state.referralHistoryLoading = true;
    })
    .addCase(deleteAssesmentTool.fulfilled, (state: MedicalHistoryState, action: any) => {
      state.referralHistory.assessment_tools = [
        ...(state.referralHistory.assessment_tools || []),
      ].filter((tool) => {
        return tool.id !== action.meta.arg.toolId;
      });
      state.referralHistoryLoading = false;
    })
    .addCase(deleteAssesmentTool.rejected, (state: MedicalHistoryState, action: any) => {
      state.error = action.error;
      state.referralHistoryLoading = false;
      toast.error(state.error.message);
    });
}

export const medicalHistoryActions = Object.assign(extraActions, slice.actions);
export const medicalHistoryReducer = slice.reducer;
